
import { supabase } from '@/integrations/supabase/client';
import { saveToLocalStorageFallback, getFromLocalStorage } from '../utils';

export interface Recharge {
  recipientNumber: string;
  cardNumbers: string;
  date: string;
}

/**
 * Get or create user ID from email for custom authentication system
 */
async function getUserIdFromEmail(email: string): Promise<string | null> {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('id')
      .eq('email', email)
      .maybeSingle();

    if (error || !data) {
      console.error('Error getting user ID from email:', error);
      return null;
    }

    return data.id;
  } catch (err) {
    console.error('Exception when getting user ID:', err);
    return null;
  }
}

/**
 * Function to save recharge data to Supabase with custom auth
 */
export const saveRechargeToSupabase = async (recharge: Recharge, userEmail?: string): Promise<boolean> => {
  try {
    if (!userEmail) {
      console.error('No user email provided for saving recharge');
      saveToLocalStorageFallback('recharges', recharge);
      saveToLocalStorageFallback('rechargeCards', recharge);
      return false;
    }

    const userId = await getUserIdFromEmail(userEmail);
    if (!userId) {
      console.error('No user ID found for email:', userEmail);
      saveToLocalStorageFallback('recharges', recharge);
      saveToLocalStorageFallback('rechargeCards', recharge);
      return false;
    }

    // Map the recharge object to match Supabase table column names
    const rechargeData = {
      recipientnumber: recharge.recipientNumber,
      cardnumbers: recharge.cardNumbers,
      date: recharge.date,
      user_id: userId
    };
    
    // Save to recharges table
    const { error: rechargeError } = await supabase
      .from('recharges')
      .insert([rechargeData]);
    
    if (rechargeError) {
      console.error('Error saving recharge to Supabase:', rechargeError);
    }
    
    // Save to rechargecards table as well
    const { error: rechargeCardError } = await supabase
      .from('rechargecards')
      .insert([rechargeData]);
    
    if (rechargeCardError) {
      console.error('Error saving recharge card to Supabase:', rechargeCardError);
      
      // Fallback to localStorage
      saveToLocalStorageFallback('recharges', recharge);
      saveToLocalStorageFallback('rechargeCards', recharge);
      
      return false;
    }
    
    return true;
  } catch (err) {
    console.error('Exception when saving recharge to Supabase:', err);
    
    // Fallback to localStorage
    saveToLocalStorageFallback('recharges', recharge);
    saveToLocalStorageFallback('rechargeCards', recharge);
    
    return false;
  }
};

/**
 * Function to get recharges from Supabase with custom auth
 */
export const getRechargesFromSupabase = async (date?: Date, userEmail?: string) => {
  try {
    if (!userEmail) {
      console.log('No user email provided, falling back to localStorage');
      return getFromLocalStorage<Recharge>('recharges');
    }

    const userId = await getUserIdFromEmail(userEmail);
    if (!userId) {
      console.log('No user ID found, falling back to localStorage');
      return getFromLocalStorage<Recharge>('recharges');
    }

    let query = supabase
      .from('recharges')
      .select('*')
      .eq('user_id', userId)
      .order('date', { ascending: false });
    
    if (date) {
      const startOfDay = new Date(date);
      startOfDay.setHours(0, 0, 0, 0);
      
      const endOfDay = new Date(date);
      endOfDay.setHours(23, 59, 59, 999);
      
      query = query
        .gte('date', startOfDay.toISOString())
        .lte('date', endOfDay.toISOString());
    }
    
    const { data, error } = await query;
    
    if (error) {
      console.error('Error getting recharges from Supabase:', error);
      return getFromLocalStorage<Recharge>('recharges');
    }
    
    return data || [];
  } catch (err) {
    console.error('Exception when getting recharges from Supabase:', err);
    return getFromLocalStorage<Recharge>('recharges');
  }
};

/**
 * Function to get recharge cards from Supabase with custom auth
 */
export const getRechargeCardsFromSupabase = async (date?: Date, userEmail?: string) => {
  try {
    if (!userEmail) {
      console.log('No user email provided, falling back to localStorage');
      return getFromLocalStorage<Recharge>('rechargeCards');
    }

    const userId = await getUserIdFromEmail(userEmail);
    if (!userId) {
      console.log('No user ID found, falling back to localStorage');
      return getFromLocalStorage<Recharge>('rechargeCards');
    }

    let query = supabase
      .from('rechargecards')
      .select('*')
      .eq('user_id', userId)
      .order('date', { ascending: false });
    
    if (date) {
      const startOfDay = new Date(date);
      startOfDay.setHours(0, 0, 0, 0);
      
      const endOfDay = new Date(date);
      endOfDay.setHours(23, 59, 59, 999);
      
      query = query
        .gte('date', startOfDay.toISOString())
        .lte('date', endOfDay.toISOString());
    }
    
    const { data, error } = await query;
    
    if (error) {
      console.error('Error getting recharge cards from Supabase:', error);
      return getFromLocalStorage<Recharge>('rechargeCards');
    }
    
    return data || [];
  } catch (err) {
    console.error('Exception when getting recharge cards from Supabase:', err);
    return getFromLocalStorage<Recharge>('rechargeCards');
  }
};
